import ProductHero from "@/components/ProductHero";
import ProductDetail from "@/components/ProductDetail";
import ProductCTA from "@/components/ProductCTA";
import SEO from "@/components/SEO";
import SystemCombinations from "@/components/SystemCombinations";
import RelatedSystems from "@/components/RelatedSystems";
import GeoDefinition, { buildDefinedTerm, buildFaqJsonLd } from "@/components/GeoDefinition";
import ProductFaq from "@/components/ProductFaq";
import {
  IntroBlock,
  ProblemBlock,
  ExperienceBlock,
  AudienceBlock,
} from "@/components/SectionBlocks";
import { SYSTEMS } from "@/lib/constants";

const s = SYSTEMS.find((x) => x.slug === "pvra");

const PVRA_DEFINITION =
  "PVRA (PINE Virtual Reality Arena) ist das große Virtual-Reality-Arena-System von PINE Systems. Es ist für größere Freizeitflächen, professionelle Betreiberstrukturen und Standorte gedacht, die mehrere Erlebnisformate – von Escape Rooms über Familienmissionen bis zu Teamduellen – in einem betreuten Arena-Konzept bündeln möchten.";

const PVRA_PRODUCT_FAQ = [
  {
    q: "Für welche Standorte ist PVRA gedacht?",
    a: "PVRA richtet sich an größere Freizeitflächen, Indoor-Entertainment-Standorte, Gruppen- und Eventbetriebe sowie professionelle Betreiberstrukturen, die mehrere Erlebnisformate parallel betreiben wollen.",
  },
  {
    q: "Ist PVRA nur für große Betreiber geeignet?",
    a: "PVRA ist ein systematisiertes Arena-Konzept und entfaltet seinen Vorteil dort, wo eine größere Fläche, mehrere Buchungsanlässe und eine professionelle Betriebsstruktur zusammenkommen. Für kompaktere Flächen sind PINE VR oder Hotel VR die passendere Antwort.",
  },
  {
    q: "Welche Erlebnisse können in einer PVRA angeboten werden?",
    a: "Virtuelle Escape Rooms, kooperative Abenteuer- und Rätselmissionen, familiengeeignete Gruppenformate, Party- und Minigame-Formate, Teamduelle, Action- und Bewegungsformate, Firmen- und Teambuilding-Erlebnisse sowie Event- und Gruppenbuchungen.",
  },
  {
    q: "Wie wird geprüft, ob eine Fläche zu PVRA passt?",
    a: "Über die Standortprüfung: Fläche, Raumhöhe, Grundriss, Gästewege, Infrastruktur, Betreiberlogik, Zielgruppe und Customer Journey werden gemeinsam eingeordnet, bevor eine Eignung benannt wird.",
  },
];

const PVRA_QA_FOR_LD = [
  {
    q: "Für welche Standorte ist PVRA geeignet?",
    a: "Für größere Freizeitstandorte, Indoor-Entertainment-Flächen, Gruppen- und Eventstandorte sowie professionelle Betreiberstrukturen mit ausreichender Fläche und Personalplanung.",
  },
  {
    q: "Wie groß muss die Fläche für PVRA sein?",
    a: "Orientierungswert ab ca. 350 m² Gesamtfläche bei passenden Standortparametern. Bis zu 16 gleichzeitige Spieler auf zwei Playfields.",
  },
  ...PVRA_PRODUCT_FAQ,
];

const PRODUCT_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "PVRA — PINE Virtual Reality Arena",
    brand: { "@type": "Brand", name: "PINE Systems" },
    description: PVRA_DEFINITION,
    category: "Location-Based Entertainment System",
  },
  buildDefinedTerm({
    name: "PVRA",
    alternateName: "PINE Virtual Reality Arena",
    description: PVRA_DEFINITION,
    url: "https://www.pinexs.de/pvra",
  }),
  buildFaqJsonLd(PVRA_QA_FOR_LD),
];

export default function Pvra() {
  return (
    <>
      <SEO
        title="PVRA – Das große Virtual-Reality-Arena-System von PINE Systems"
        description="PVRA ist das große Virtual-Reality-Arena-System für größere Freizeitflächen, Gruppenangebote, Events und professionelle Betreiberstrukturen."
        ogType="product"
        jsonLd={PRODUCT_JSONLD}
      />
      <ProductHero
        code="01 / SYSTEM"
        name="PVRA"
        headline="Das große Virtual-Reality-Arena-System von PINE Systems."
        sub="Für größere Freizeitstandorte, Betreiberstrukturen und Erlebnisflächen, die ein vollwertiges VR-Arena-Angebot mit mehreren Erlebnisformaten aufbauen möchten."
        image={s.image}
      />

      <IntroBlock
        overline="Positionierung"
        title="Für Standorte, die aus Fläche ein vollwertiges Erlebnisangebot machen wollen."
        text={`PVRA ist das große Virtual-Reality-Arena-System von PINE Systems. Es richtet sich an Betreiber, die nicht nur ein einzelnes VR-Angebot integrieren möchten, sondern einen eigenständigen Erlebnisstandort mit mehreren Buchungsanlässen aufbauen wollen.

PVRA ist für Standorte gedacht, an denen Gruppen, Familien, Firmen, Events und wiederkehrende Besucher eine klare Rolle spielen.`}
      />

      <ProblemBlock
        overline="Problem"
        title="Das Problem, das PVRA löst."
        text="Viele größere Freizeitflächen haben Platz, aber kein modernes Angebot mit klarer Wiederholbarkeit. Klassische Freizeitangebote geraten unter Druck, weil Gäste mehr erwarten als passive Beschäftigung. Gleichzeitig reicht ein einzelnes technisches Highlight nicht aus, um langfristig Buchungsanlässe zu schaffen. PVRA löst dieses Problem durch ein systematisiertes Arena-Konzept mit mehreren Erlebnisformaten, betreutem Ablauf und klarer Betreiberlogik."
        cards={[
          {
            title: "Große Flächen brauchen Erlebnisdichte",
            text: "Mehr Quadratmeter allein erzeugen keinen Wert. Entscheidend ist, wie daraus buchbare Erlebnisse entstehen.",
          },
          {
            title: "Gruppen brauchen klare Anlässe",
            text: "Geburtstage, Firmen, Familien, Teams und Events brauchen unterschiedliche Zugänge zum gleichen Standort.",
          },
          {
            title: "Betrieb braucht Wiederholbarkeit",
            text: "Ein Standort wird erst dann skalierbar, wenn Ablauf, Einweisung, Betreuung und Qualität reproduzierbar sind.",
          },
        ]}
      />

      <ExperienceBlock
        overline="Erlebnisformate"
        title="Welche Erlebnisse PVRA möglich macht."
        text="PVRA kann unterschiedliche Erlebnisrichtungen bündeln und dadurch mehrere Zielgruppen bedienen."
        items={[
          "Virtuelle Escape Rooms",
          "Kooperative Abenteuer- und Rätselmissionen",
          "Familiengeeignete Gruppenformate",
          "Party- und Minigame-Formate",
          "Teamduelle und Competition",
          "Action- und Bewegungsformate",
          "Firmen- und Teambuilding-Erlebnisse",
          "Event- und Gruppenbuchungen",
        ]}
        highlight="Ein Standort. Mehrere Buchungsanlässe. PVRA ist besonders stark, wenn ein Betreiber verschiedene Zielgruppen über ein gemeinsames Standortsystem ansprechen möchte: Familien am Wochenende, Firmen unter der Woche, Gruppen am Abend, Events nach Anfrage."
      />

      <AudienceBlock
        overline="Zielgruppen"
        title="Für welche Betreiber PVRA besonders passt."
        cards={[
          {
            title: "Freizeitflächen",
            text: "Für Betreiber, die eine größere Fläche in einen modernen Erlebnisstandort verwandeln möchten.",
          },
          {
            title: "Gründer",
            text: "Für Gründer, die nicht nur ein Einzelangebot, sondern ein ganzes Arena-Konzept aufbauen wollen.",
          },
          {
            title: "Event- und Gruppenstandorte",
            text: "Für Standorte, die Firmen, Geburtstage, JGA, Vereine und private Gruppen ansprechen möchten.",
          },
          {
            title: "Bestehende Indoor-Anbieter",
            text: "Für Betriebe, die ihr Angebot modernisieren und neue Zielgruppen erschließen möchten.",
          },
        ]}
      />

      <ProductDetail
        forWhom={[
          "Größere Freizeitflächen",
          "Indoor-Entertainment",
          "Gruppen- und Eventstandorte",
          "Professionelle Betreiberstrukturen",
          "Gründer mit ausreichender Fläche",
          "Familien-, Firmen- und Eventzielgruppen",
        ]}
        strengths={[
          "Breite Zielgruppenlogik",
          "Mehrere Erlebnisformate",
          "Wiederholbare Angebotsstruktur",
          "Professioneller Betrieb",
          "Betreute Arena-Logik",
        ]}
        orientation={[
          "Bis zu 16 gleichzeitige Spieler auf zwei Playfields",
          "Ab 350 m² Gesamtfläche bei passenden Standortparametern",
          "Finale Eignung nach Standortprüfung",
        ]}
        ctaLabel="PVRA-Standort prüfen lassen"
      />

      <ProductCTA
        title="Ist Ihre Fläche für PVRA geeignet?"
        text="PVRA ist ein großes Systemprofil. Die Eignung entsteht aus Fläche, Raumhöhe, Grundriss, Betreiberlogik, Zielgruppe, Infrastruktur und Personalplanung."
        primaryLabel="PVRA-Standort prüfen lassen"
      />

      <RelatedSystems currentSlug="pvra" />
      <SystemCombinations />

      <ProductFaq
        productSlug="pvra"
        title="Häufige Fragen zu PVRA."
        qa={PVRA_PRODUCT_FAQ}
      />

      <GeoDefinition
        term="PVRA"
        overline="GEO · Definition"
        definition={PVRA_DEFINITION}
        qa={PVRA_QA_FOR_LD.slice(0, 2)}
        variant="muted"
      />
    </>
  );
}
